import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';
import { formatFromFilter, formatToFilter } from '@app/utils/formatDate';

interface StockingJournalFilterState {
  selectedFishtypeIds: number[];
  selectedWaterbodyIds: number[];
  selectedAgeIds: number[];
  fromDate: Dayjs | null | undefined;
  toDate: Dayjs | null | undefined;
  selectedFishtypeIdsTemporal: number[];
  selectedWaterbodyIdsTemporal: number[];
  selectedAgeIdsTemporal: number[];
  fromDateTemporal: Dayjs | null | undefined;
  toDateTemporal: Dayjs | null | undefined;
  shouldTriggerFetch: boolean;
  filterOptions: {
    uniqueFishtypes: Array<{ id: number; name_DE: string }>;
    uniqueWaterbodies: Array<{ id: number; nameDe: string }>;
    uniqueAges: Array<{ id: number; nameDe: string; nameEn: string; nameFr: string; nameIt: string }>;
  };
}

const initialState: StockingJournalFilterState = {
  selectedFishtypeIds: [],
  selectedWaterbodyIds: [],
  selectedAgeIds: [],
  fromDate: undefined,
  toDate: undefined,
  selectedFishtypeIdsTemporal: [],
  selectedWaterbodyIdsTemporal: [],
  selectedAgeIdsTemporal: [],
  fromDateTemporal: undefined,
  toDateTemporal: undefined,
  shouldTriggerFetch: true,
  filterOptions: {
    uniqueFishtypes: [],
    uniqueWaterbodies: [],
    uniqueAges: [],
  },
};

const stockingJournalFiltersSlice = createSlice({
  name: 'stockingJournalFilters',
  initialState,
  reducers: {
    setSelectedFishtypeIds(state, action: PayloadAction<number[]>) {
      state.selectedFishtypeIds = action.payload;
    },
    setSelectedWaterbodyIds(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedAgeIds(state, action: PayloadAction<number[]>) {
      state.selectedAgeIds = action.payload;
    },
    setFromDate(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromDate = action.payload;
    },
    setToDate(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toDate = action.payload;
    },
    setSelectedFishtypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedFishtypeIdsTemporal = action.payload;
    },
    setSelectedWaterbodyIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedWaterbodyIdsTemporal = action.payload;
    },
    setSelectedAgeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedAgeIdsTemporal = action.payload;
    },
    setFromDateTemporal(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromDateTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToDateTemporal(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toDateTemporal = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.selectedFishtypeIds = initialState.selectedFishtypeIds;
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedAgeIds = initialState.selectedAgeIds;
      state.fromDate = initialState.fromDate;
      state.toDate = initialState.toDate;
      state.selectedFishtypeIdsTemporal = initialState.selectedFishtypeIdsTemporal;
      state.selectedWaterbodyIdsTemporal = initialState.selectedWaterbodyIdsTemporal;
      state.selectedAgeIdsTemporal = initialState.selectedAgeIdsTemporal;
      state.fromDateTemporal = initialState.fromDateTemporal;
      state.toDateTemporal = initialState.toDateTemporal;
      state.shouldTriggerFetch = true;
    },
    applyFilters(state) {
      state.selectedFishtypeIds = state.selectedFishtypeIdsTemporal;
      state.selectedWaterbodyIds = state.selectedWaterbodyIdsTemporal;
      state.selectedAgeIds = state.selectedAgeIdsTemporal;
      state.fromDate = state.fromDateTemporal;
      state.toDate = state.toDateTemporal;
      state.shouldTriggerFetch = true;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setFilterOptions(
      state,
      action: PayloadAction<{
        uniqueFishtypes?: Array<{ id: number; name_DE: string }>;
        uniqueWaterbodies?: Array<{ id: number; nameDe: string }>;
        uniqueAges?: Array<{ id: number; nameDe: string; nameEn: string; nameFr: string; nameIt: string }>;
      }>,
    ) {
      if (action.payload.uniqueFishtypes) {
        state.filterOptions.uniqueFishtypes = action.payload.uniqueFishtypes;
      }
      if (action.payload.uniqueWaterbodies) {
        state.filterOptions.uniqueWaterbodies = action.payload.uniqueWaterbodies;
      }
      if (action.payload.uniqueAges) {
        state.filterOptions.uniqueAges = action.payload.uniqueAges;
      }
    },
  },
});

export const {
  setSelectedFishtypeIds,
  setSelectedWaterbodyIds,
  setSelectedAgeIds,
  setFromDate,
  setToDate,
  setSelectedFishtypeIdsTemporal,
  setSelectedWaterbodyIdsTemporal,
  setSelectedAgeIdsTemporal,
  setFromDateTemporal,
  setToDateTemporal,
  resetFilters,
  applyFilters,
  setShouldTriggerFetch,
  setFilterOptions,
} = stockingJournalFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { stockingJournalFilters: StockingJournalFilterState }) => {
  const { selectedFishtypeIds, selectedWaterbodyIds, selectedAgeIds, fromDate, toDate } = state.stockingJournalFilters;

  return (
    selectedFishtypeIds.length > 0 ||
    selectedWaterbodyIds.length > 0 ||
    selectedAgeIds.length > 0 ||
    !!fromDate ||
    !!toDate
  );
};

export const activeFiltersCountSelector = (state: { stockingJournalFilters: StockingJournalFilterState }) => {
  const { selectedFishtypeIds, selectedWaterbodyIds, selectedAgeIds, fromDate, toDate } = state.stockingJournalFilters;

  let count = 0;
  if (selectedFishtypeIds.length > 0) count++;
  if (selectedWaterbodyIds.length > 0) count++;
  if (selectedAgeIds.length > 0) count++;
  if (fromDate) count++;
  if (toDate) count++;

  return count;
};

export const fetchParamsSelector = (state: { stockingJournalFilters: StockingJournalFilterState }) => {
  const {
    selectedFishtypeIdsTemporal,
    selectedWaterbodyIdsTemporal,
    selectedAgeIdsTemporal,
    fromDateTemporal,
    toDateTemporal,
  } = state.stockingJournalFilters;

  const params: {
    fishtype?: number;
    waterbody?: number;
    age?: number;
    from?: string;
    to?: string;
  } = {};

  // The API expects single IDs for fishtype, waterbody, age (based on the API endpoint)
  // If multiple are selected, we'll use the first one
  if (selectedFishtypeIdsTemporal.length > 0) {
    params.fishtype = selectedFishtypeIdsTemporal[0];
  }

  if (selectedWaterbodyIdsTemporal.length > 0) {
    params.waterbody = selectedWaterbodyIdsTemporal[0];
  }

  if (selectedAgeIdsTemporal.length > 0) {
    params.age = selectedAgeIdsTemporal[0];
  }

  if (fromDateTemporal) {
    params.from = formatFromFilter(fromDateTemporal);
  }

  if (toDateTemporal) {
    params.to = formatToFilter(toDateTemporal);
  }

  return params;
};

export const stockingJournalFiltersActions = stockingJournalFiltersSlice.actions;

export const stockingJournalFilters = stockingJournalFiltersSlice.reducer;
